export interface OperationCapability
{
    /** Chosen from the documented operation/environment contract before any request runs. */
    readonly transport: 'secure' | 'https';
    /** HTTP method alone is insufficient: paginated reads can use POST. No write is assumed idempotent. */
    readonly semantics: 'safe-read' | 'write';
}

/** Structurally compatible with common/session's SessionAttempt and transport key-exchange attempts. */
export interface OperationLifetime
{
    readonly cancelled: Promise<void>;
    /** Must remain false once superseded; check the captured account/document/session generation. */
    isCurrent(): boolean;
}

export interface OperationAttempt extends OperationLifetime
{
    /** At most two attempts for a safe read, at most one for a write. Cancellation may prevent dispatch. */
    readonly number: 1 | 2;
}

export interface OperationFailure
{
    readonly stage: 'prepare' | 'dispatch' | 'decode';
    readonly cause: unknown;
    /** True as soon as dispatch is entered, including synchronous throws and lost responses. */
    readonly dispatched: boolean;
}

type MaybePromise<T> = T | Promise<T>;

export interface OperationAdapter<Prepared, Wire, Result>
{
    /** Wait for readiness and prepare/encrypt using captured credentials/context. Never send the operation here. */
    prepare(attempt: OperationAttempt): MaybePromise<Prepared>;
    /** Exactly one HTTP dispatch; adapter/HTTP middleware must not add their own retries or fallback. */
    dispatch(request: Prepared, attempt: OperationAttempt): MaybePromise<Wire>;
    /** Decode/decrypt only; callers recheck their lifetime before publishing the resolved result. */
    decode(response: Wire, request: Prepared, attempt: OperationAttempt): MaybePromise<Result>;
    /**
     * Called only after the first safe-read failure. Classify the failure before doing recovery;
     * return true only after successful bounded recovery permits another attempt in this lifetime.
     * Return false for terminal failures. Never send the operation or change its transport here.
     * Writes surface their original failure; callers may recover session state separately without replay.
     */
    recover?(failure: OperationFailure, attempt: OperationAttempt): MaybePromise<boolean>;
}

export type OperationPolicyErrorCode = 'invalid-capability' | 'unsupported-operation' | 'unsupported-transport' | 'cancelled';
export type OperationPolicyError = Error & { readonly code: OperationPolicyErrorCode; readonly cause?: unknown; };

function policyError(code: OperationPolicyErrorCode, cause?: unknown): OperationPolicyError
{
    return Object.assign(new Error(`Transport operation: ${code}`), { code, cause });
}

/**
 * Framework-neutral capability selection and replay limits, with no URLs, credentials or crypto globals.
 * Adapters supply an explicit operation table per environment; unknown operations fail closed.
 * There is no secure-to-HTTPS fallback and no automatic write replay, even after a decode failure.
 */
export class TransportOperationPolicy
{
    private readonly capabilities = new Map<string, OperationCapability>();

    public constructor(capabilities: Readonly<Record<string, OperationCapability>>)
    {
        for (const operation of Object.keys(capabilities))
        {
            const capability = capabilities[operation];
            if (!capability || (capability.transport !== 'secure' && capability.transport !== 'https')
                || (capability.semantics !== 'safe-read' && capability.semantics !== 'write'))
            {
                throw policyError('invalid-capability');
            }
            this.capabilities.set(operation, Object.freeze({ transport: capability.transport, semantics: capability.semantics }));
        }
    }

    /**
     * The lifetime spans preparation, dispatch, decoding and any safe-read recovery. A generation
     * replacement cancels this invocation; it never reassigns a prepared request to another account.
     * Cancellation settles promptly but cannot undo an already-dispatched write. Hooks must honour
     * attempt.isCurrent()/cancelled at their own asynchronous boundaries and use captured credentials.
     */
    public execute<Prepared, Wire, Result>(
        operation: string,
        adapters: { readonly secure?: OperationAdapter<Prepared, Wire, Result>; readonly https?: OperationAdapter<Prepared, Wire, Result>; },
        lifetime: OperationLifetime,
    ): Promise<Result>
    {
        return new Promise<Result>((resolve, reject) => {
            const capability = this.capabilities.get(operation);
            if (!capability) throw policyError('unsupported-operation');
            const adapter = adapters[capability.transport];
            if (!adapter) throw policyError('unsupported-transport');

            let active = true;
            let cancellationError: OperationPolicyError | null = null;
            let notifyCancellation!: () => void;
            const cancelled = new Promise<void>(resolve => { notifyCancellation = resolve; });
            const cancel = (cause?: unknown): void => {
                if (!active || cancellationError) return;
                cancellationError = policyError('cancelled', cause);
                notifyCancellation();
                active = false;
                reject(cancellationError);
            };
            const isCurrent = (): boolean => {
                if (!active || cancellationError) return false;
                try { if (!lifetime.isCurrent()) cancel(); }
                catch (cause) { cancel(cause); }
                return cancellationError === null;
            };
            const requireCurrent = (): void => {
                if (!isCurrent()) throw cancellationError ?? policyError('cancelled');
            };
            void lifetime.cancelled.then(() => cancel(), cause => cancel(cause));

            const run = (number: 1 | 2): Promise<Result> => {
                const attempt: OperationAttempt = Object.freeze({ number, cancelled, isCurrent });
                let stage: OperationFailure['stage'] = 'prepare';
                let dispatched = false;
                let request: Prepared;
                return Promise.resolve().then(() => {
                    requireCurrent();
                    return adapter.prepare(attempt);
                }).then(prepared => {
                    requireCurrent();
                    request = prepared;
                    stage = 'dispatch';
                    dispatched = true;
                    return adapter.dispatch(request, attempt);
                }).then(response => {
                    requireCurrent();
                    stage = 'decode';
                    return adapter.decode(response, request, attempt);
                }).then(result => {
                    requireCurrent();
                    return result;
                }).catch(cause => {
                    requireCurrent();
                    if (capability.semantics !== 'safe-read' || number === 2 || !adapter.recover) throw cause;
                    return Promise.resolve(adapter.recover(Object.freeze({ stage, cause, dispatched }), attempt)).then(retry => {
                        requireCurrent();
                        if (retry !== true) throw cause;
                        return run(2);
                    });
                });
            };

            // Check immediately before settling the public promise. Observe the driver even when
            // cancellation settles first, so late hook failures cannot leak or resume old work.
            void run(1).then(result => {
                requireCurrent();
                active = false;
                resolve(result);
            }).catch(cause => {
                active = false;
                reject(cancellationError ?? cause);
            });
        });
    }
}
